import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  message,
  Modal,
  Form,
  Typography,
  Popconfirm,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  MailOutlined,
  ReloadOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { logger } from '@/shared/lib/logger';
import type { ColumnsType } from 'antd/es/table';
import { mcmContactApiService } from '../services/mcmContactApiService';
import type { McmContact } from '../types/mcm-contact.types';
import { useResponsive } from '@/shared/hooks/useResponsive';

const { Text } = Typography;

const COMPONENT_CONSTANTS = {
  EMAIL_MAX_LENGTH: 255,
  MAX_REGISTRABLE_CONTACT_COUNT: 2,
  TABLE_WIDTH_ID: 80,
  TABLE_WIDTH_ID_MOBILE: 60,
  TABLE_WIDTH_ACTION: 120,
  TABLE_WIDTH_ACTION_MOBILE: 90,
  PAGINATION_PAGE_SIZE_DESKTOP: 10,
  PAGINATION_PAGE_SIZE_MOBILE: 5,
} as const;

export const McmContactManager: React.FC = () => {
  const [contacts, setContacts] = useState<McmContact[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editingContact, setEditingContact] = useState<McmContact | null>(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const { isMobile } = useResponsive();

  /**
   * MCM連絡先一覧を取得
   */
  const fetchContacts = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await mcmContactApiService.getAll();
      if (response.success) {
        setContacts(response.data);
      }
    } catch (error) {
      message.error('MCM連絡先の取得に失敗しました');
      logger.captureError(error, 'McmContactManager.fetchContacts');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * MCM連絡先を追加
   */
  const handleAddContact = async (values: { email: string }): Promise<void> => {
    try {
      const response = await mcmContactApiService.create({
        request: {
          email: values.email.trim(),
        },
      });
      if (response.success) {
        message.success(response.message);
        setIsModalOpen(false);
        form.resetFields();
        await fetchContacts();
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        message.error(error.message || 'MCM連絡先の追加に失敗しました');
      } else {
        message.error('MCM連絡先の追加に失敗しました');
      }
      logger.captureError(error, 'McmContactManager.handleAddContact');
    }
  };

  /**
   * MCM連絡先を更新
   */
  const handleUpdateContact = async (values: { email: string }): Promise<void> => {
    if (editingContact === null) {
      return;
    }

    try {
      const response = await mcmContactApiService.update({
        id: editingContact.id,
        request: {
          email: values.email.trim(),
        },
      });
      if (response.success) {
        message.success(response.message);
        setIsEditModalOpen(false);
        setEditingContact(null);
        editForm.resetFields();
        await fetchContacts();
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        message.error(error.message || 'MCM連絡先の更新に失敗しました');
      } else {
        message.error('MCM連絡先の更新に失敗しました');
      }
      logger.captureError(error, 'McmContactManager.handleUpdateContact');
    }
  };

  /**
   * MCM連絡先を削除
   */
  const handleDeleteContact = async (id: number): Promise<void> => {
    try {
      const response = await mcmContactApiService.delete({ id });
      if (response.success) {
        message.success(response.message);
        await fetchContacts();
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        message.error(error.message || 'MCM連絡先の削除に失敗しました');
      } else {
        message.error('MCM連絡先の削除に失敗しました');
      }
      logger.captureError(error, 'McmContactManager.handleDeleteContact');
    }
  };

  /**
   * モーダルを開く
   */
  const handleOpenModal = (): void => {
    setIsModalOpen(true);
  };

  /**
   * モーダルを閉じる
   */
  const handleCloseModal = (): void => {
    setIsModalOpen(false);
    form.resetFields();
  };

  /**
   * 編集モーダルを開く
   */
  const handleOpenEditModal = (contact: McmContact): void => {
    setEditingContact(contact);
    editForm.setFieldsValue({ email: contact.email });
    setIsEditModalOpen(true);
  };

  /**
   * 編集モーダルを閉じる
   */
  const handleCloseEditModal = (): void => {
    setIsEditModalOpen(false);
    setEditingContact(null);
    editForm.resetFields();
  };

  /**
   * 初回データ取得（クリーンアップ対応）
   */
  useEffect(() => {
    let isMounted = true;

    const loadContacts = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const response = await mcmContactApiService.getAll();
        if (isMounted && response.success) {
          setContacts(response.data);
        }
      } catch (error) {
        if (isMounted) {
          message.error('MCM連絡先の取得に失敗しました');
          logger.captureError(error, 'McmContactManager.useEffect.loadContacts');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadContacts();

    return () => {
      isMounted = false;
    };
  }, []);

  const isAddLimitReached = contacts.length >= COMPONENT_CONSTANTS.MAX_REGISTRABLE_CONTACT_COUNT;
  const addLimitReachedMessage = `連絡先メールアドレスは最大${COMPONENT_CONSTANTS.MAX_REGISTRABLE_CONTACT_COUNT}件まで登録できます`;

  const columns: ColumnsType<McmContact> = [
    ...(!isMobile ? [{
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: COMPONENT_CONSTANTS.TABLE_WIDTH_ID,
    }] : []),
    {
      title: 'メールアドレス',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => (
        <Space>
          <MailOutlined />
          <Text>{email}</Text>
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: isMobile ? COMPONENT_CONSTANTS.TABLE_WIDTH_ACTION_MOBILE : COMPONENT_CONSTANTS.TABLE_WIDTH_ACTION,
      render: (_: unknown, record: McmContact) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleOpenEditModal(record)}
          >
            {!isMobile && '編集'}
          </Button>
          <Popconfirm
            title="MCM連絡先を削除"
            description={`「${record.email}」を削除してもよろしいですか？`}
            onConfirm={() => handleDeleteContact(record.id)}
            okText="削除"
            cancelText="キャンセル"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />} size="small">
              {!isMobile && '削除'}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title={
          <Space>
            <MailOutlined />
            {!isMobile && 'MCM管理者通知先メールアドレス'}
            {isMobile && 'MCM連絡先'}
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchContacts}
              loading={isLoading}
            >
              {!isMobile && '更新'}
            </Button>
            <Tooltip title={isAddLimitReached ? addLimitReachedMessage : undefined}>
              {/* disabledなButtonは自身がホバーイベントを発火しないため、Tooltipのトリガーをspanで用意する */}
              <span>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleOpenModal}
                  disabled={isAddLimitReached}
                >
                  {!isMobile && '追加'}
                </Button>
              </span>
            </Tooltip>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={contacts}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: isMobile ? COMPONENT_CONSTANTS.PAGINATION_PAGE_SIZE_MOBILE : COMPONENT_CONSTANTS.PAGINATION_PAGE_SIZE_DESKTOP,
            showTotal: (total) => `合計 ${total} 件`,
          }}
          scroll={isMobile ? { x: true } : undefined}
          size={isMobile ? 'small' : 'middle'}
        />
      </Card>

      <Modal
        title="MCM連絡先を追加"
        open={isModalOpen}
        onCancel={handleCloseModal}
        onOk={() => form.submit()}
        okText="追加"
        cancelText="キャンセル"
        width={isMobile ? '90%' : 520}
      >
        <Form form={form} layout="vertical" onFinish={handleAddContact}>
          <Form.Item
            name="email"
            label="メールアドレス"
            rules={[
              { required: true, message: 'メールアドレスを入力してください' },
              { type: 'email', message: '正しいメールアドレス形式を入力してください' },
              {
                max: COMPONENT_CONSTANTS.EMAIL_MAX_LENGTH,
                message: `メールアドレスは${COMPONENT_CONSTANTS.EMAIL_MAX_LENGTH}文字以内で入力してください`,
              },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="admin@mcm-company.com"
              maxLength={COMPONENT_CONSTANTS.EMAIL_MAX_LENGTH}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="MCM連絡先を編集"
        open={isEditModalOpen}
        onCancel={handleCloseEditModal}
        onOk={() => editForm.submit()}
        okText="更新"
        cancelText="キャンセル"
        width={isMobile ? '90%' : 520}
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdateContact}>
          <Form.Item
            name="email"
            label="メールアドレス"
            rules={[
              { required: true, message: 'メールアドレスを入力してください' },
              { type: 'email', message: '正しいメールアドレス形式を入力してください' },
              {
                max: COMPONENT_CONSTANTS.EMAIL_MAX_LENGTH,
                message: `メールアドレスは${COMPONENT_CONSTANTS.EMAIL_MAX_LENGTH}文字以内で入力してください`,
              },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="admin@mcm-company.com"
              maxLength={COMPONENT_CONSTANTS.EMAIL_MAX_LENGTH}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
